const DEPTH_TRUNCATED_MARKER = '[Truncated: max depth reached]';
const moreMarker = (count: number) => `… ${count} more`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type JSONValue = any;

const truncateValue = (
  value: JSONValue,
  depth: number,
  maxDepth: number,
  maxKeysPerLevel: number,
): JSONValue => {
  if (depth > maxDepth) {
    return DEPTH_TRUNCATED_MARKER;
  }

  if (Array.isArray(value)) {
    const truncated = value
      .slice(0, maxKeysPerLevel)
      .map(item => truncateValue(item, depth + 1, maxDepth, maxKeysPerLevel));
    if (value.length > maxKeysPerLevel) {
      truncated.push(moreMarker(value.length - maxKeysPerLevel));
    }
    return truncated;
  }

  if (value && typeof value === 'object') {
    const keys = Object.keys(value);
    const result: Record<string, JSONValue> = {};
    keys.slice(0, maxKeysPerLevel).forEach(key => {
      result[key] = truncateValue(
        value[key],
        depth + 1,
        maxDepth,
        maxKeysPerLevel,
      );
    });
    if (keys.length > maxKeysPerLevel) {
      result.__truncated_keys__ = moreMarker(keys.length - maxKeysPerLevel);
    }
    return result;
  }

  return value;
};

interface SerializeSchemaFragmentOptions {
  maxDepth?: number;
  maxKeysPerLevel?: number;
  maxStringLength?: number;
}

/**
 * Bounded serialization of a JSON Schema fragment for Sentry `extra`.
 * Sentry's global `normalizeDepth` would otherwise silently collapse deeply
 * nested partner schemas to `"[Object]"`; this caps depth/key-count/array
 * length up front and hard-truncates the resulting string so the payload
 * stays legible and bounded regardless of how pathological the fragment is.
 */
const serializeSchemaFragmentForSentry = (
  property: JSONValue,
  {
    maxDepth = 4,
    maxKeysPerLevel = 20,
    maxStringLength = 2000,
  }: SerializeSchemaFragmentOptions = {},
): string => {
  const truncated = truncateValue(property, 0, maxDepth, maxKeysPerLevel);
  const serialized = JSON.stringify(truncated);

  if (serialized.length <= maxStringLength) {
    return serialized;
  }

  return `${serialized.slice(0, maxStringLength)}…[truncated]`;
};

export default serializeSchemaFragmentForSentry;
